import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Code2, Save, ToggleLeft, ToggleRight, AlertTriangle, Trash2, Database } from "lucide-react";

const AdminTracking = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState(null);
  const [wiping, setWiping] = useState(false);

  const load = () => {
    api.get("/admin/tracking-codes").then((r) => setData(r.data)).catch(() => toast.error("Could not load"));
    api.get("/admin/demo-data/audit").then((r) => setAudit(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/tracking-codes", data);
      toast.success("Code injection saved — reload the storefront to verify.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const wipeDemoData = async (kind) => {
    const labels = {
      products: "demo products (Cascade chain, Whisper hoops, etc.)",
      categories: "demo categories (Necklaces, Earrings, Rings, Bracelets)",
      blogs: "demo journal posts",
      stories: "demo story rails",
    };
    if (!window.confirm(`Permanently delete ${labels[kind]}? Your real ${kind} are kept.`)) return;
    setWiping(true);
    try {
      const r = await api.post("/admin/demo-data/clear", { [kind]: true });
      toast.success(`Cleared ${r.data?.deleted?.[kind] ?? 0} demo ${kind}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setWiping(false); }
  };

  if (!data) return <p className="text-cream/75">Loading…</p>;

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const codeInput = "w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-cream placeholder:text-cream/35 focus:border-[#C9789A] focus:outline-none transition font-mono";

  return (
    <div data-testid="admin-tracking-page">
      <p className="eyebrow text-cream/65 mb-1">Engineering</p>
      <h1 className="font-hero text-3xl text-cream flex items-center gap-3 mb-2">
        <Code2 size={26} /> Code injection & data hygiene
      </h1>
      <p className="text-cream/65 text-sm mb-8 max-w-2xl">
        Drop in analytics, pixels, and chat widgets without touching the codebase. Also a one-click broom for the demo content that ships with a fresh install.
      </p>

      {/* TRACKING CODES */}
      <section className="bg-white rounded-2xl border border-border p-6 space-y-5 mb-8 max-w-5xl" data-testid="tracking-form">
        <div className="flex items-center justify-between">
          <p className="eyebrow">Tracking & custom code</p>
          <button onClick={() => setData({ ...data, enabled: !data.enabled })} className="flex items-center gap-2 text-sm" data-testid="tracking-toggle">
            {data.enabled
              ? <><ToggleRight size={28} className="text-emerald-600" /> Active on storefront</>
              : <><ToggleLeft size={28} className="text-charcoal/50" /> Disabled</>}
          </button>
        </div>

        <div className="rounded-xl bg-blush/30 border border-rose/20 p-4 text-sm text-charcoal/80 flex gap-3">
          <AlertTriangle size={16} className="text-rose flex-shrink-0 mt-0.5" />
          <div>
            Snippets only run on the storefront, never on admin pages. Anything you paste here will be executed as-is — only paste code from sources you trust.
          </div>
        </div>

        <div>
          <label className="eyebrow">Header code <span className="normal-case tracking-normal text-charcoal/60">(rendered inside &lt;head&gt; — best for Google Analytics gtag, Search Console verification, font preloads)</span></label>
          <Textarea
            rows={6}
            value={data.head_code || ""}
            onChange={set("head_code")}
            placeholder="<!-- Google tag (gtag.js) -->\n<script async src='https://www.googletagmanager.com/gtag/js?id=G-XXXXXX'></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXXXX');\n</script>"
            className={codeInput}
            data-testid="tracking-head"
          />
        </div>

        <div>
          <label className="eyebrow">Body open code <span className="normal-case tracking-normal text-charcoal/60">(rendered just after &lt;body&gt; — best for GTM noscript / Meta Pixel noscript fallbacks)</span></label>
          <Textarea
            rows={5}
            value={data.body_open_code || ""}
            onChange={set("body_open_code")}
            placeholder="<!-- Google Tag Manager (noscript) -->\n<noscript><iframe src='https://www.googletagmanager.com/ns.html?id=GTM-XXXX' height='0' width='0' style='display:none;visibility:hidden'></iframe></noscript>"
            className={codeInput}
            data-testid="tracking-body-open"
          />
        </div>

        <div>
          <label className="eyebrow">Footer code <span className="normal-case tracking-normal text-charcoal/60">(rendered before &lt;/body&gt; — best for chat widgets like Crisp/Tawk, Hotjar, late-load analytics)</span></label>
          <Textarea
            rows={6}
            value={data.body_close_code || ""}
            onChange={set("body_close_code")}
            placeholder="<!-- Hotjar Tracking Code -->\n<script>\n  (function(h,o,t,j,a,r){...})(...)\n</script>"
            className={codeInput}
            data-testid="tracking-body-close"
          />
        </div>

        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="tracking-save">
          <Save size={14} /> {saving ? "Saving…" : "Save & inject"}
        </button>
      </section>

      {/* DEMO DATA WIPE */}
      <section className="bg-white rounded-2xl border border-border p-6 max-w-5xl" data-testid="demo-data-section">
        <div className="flex items-center gap-2 mb-3">
          <Database size={16} className="text-rose" />
          <p className="eyebrow">Demo / sample content</p>
        </div>
        <p className="text-sm text-charcoal/70 mb-5 max-w-2xl">
          When this app was first installed, it came with sample categories, products, blogs and stories so the storefront didn't look empty. Once you've added your real catalog, you can clean up here. <strong>Your real content is left untouched.</strong>
        </p>

        {audit && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { k: "products", real: audit.real_products, demo: audit.products, label: "Products" },
              { k: "categories", real: audit.real_categories, demo: audit.categories, label: "Categories" },
              { k: "blogs", demo: audit.blogs, label: "Journal posts" },
              { k: "stories", demo: audit.stories, label: "Stories" },
            ].map(({ k, real, demo, label }) => (
              <div key={k} className="rounded-xl border border-border bg-cream/40 p-4" data-testid={`audit-${k}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-charcoal/55 mb-1">{label}</p>
                <p className="font-cormorant text-2xl text-charcoal">{demo} <span className="text-sm text-charcoal/55">demo</span></p>
                {typeof real === "number" && (
                  <p className="text-xs text-emerald-700 mt-0.5">{real} real (kept)</p>
                )}
                {demo > 0 && (
                  <button
                    onClick={() => wipeDemoData(k)}
                    disabled={wiping}
                    className="text-xs text-rose hover:text-rose/80 mt-2 inline-flex items-center gap-1"
                    data-testid={`wipe-${k}`}
                  >
                    <Trash2 size={11} /> Delete demo {label.toLowerCase()}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="rounded-xl bg-cream/60 border border-border p-4 text-xs text-charcoal/65 leading-relaxed">
          <p className="font-medium text-charcoal/80 mb-1">Production deployments — important</p>
          <p>
            On a fresh install, set <code className="bg-cream px-1.5 py-0.5 rounded text-rose">BOOTSTRAP_DEMO_DATA=true</code> in your VPS env <strong>only for the very first run</strong>, then change it to <code className="bg-cream px-1.5 py-0.5 rounded">false</code>. This prevents future container restarts from re-seeding demo content alongside your real catalog.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AdminTracking;
